import { z } from 'zod';
import { type CreateGreenBeanProfileDTO, type GreenBeanProfile } from './greenBean';
import type { RoastProfile, CuppingProfile } from '../services/profileService';

export const itemTypeSchema = z.enum(['green_bean', 'roasted_bulk', 'finished_good']);

export const inventoryItemSchema = z.object({
  code: z
    .string()
    .optional()
    .transform((value) => (value && value.trim() ? value : undefined)),
  name: z.string().min(1, 'Name is required').max(100, 'Name must be at most 100 characters'),
  description: z.string().optional(),
  unitOfMeasure: z.string().min(1, 'Unit of Measure is required'),
  itemType: itemTypeSchema,
  status: z.enum(['active', 'inactive']).optional(),
  reorderPoint: z.number()
    .min(0)
    .refine((value) => Math.round(value * 1000) / 1000 === value, 'Max 3 decimal places'),
  reorderQuantity: z.number()
    .min(0)
    .refine((value) => Math.round(value * 1000) / 1000 === value, 'Max 3 decimal places')
    .optional(),
  standardCost: z.number().min(0).optional(),
  estimatedSellingPrice: z.number().min(0).optional(),
  quantity: z.number()
    .min(0)
    .refine((value) => Math.round(value * 1000) / 1000 === value, 'Max 3 decimal places'),
  expiryDate: z.string().optional(),
  workOrderId: z.string().optional(),
  woReferenceId: z.string().optional(),
  location: z.string().optional(),
  // We can't easily zod-validate the complex green bean profile here without duplicating the schema,
  // so we'll treat it as an optional unknown or any for zod, but strongly type it in TypeScript interfaces.
  green_bean_profile: z.any().optional(),
  greenBeanProfileId: z.string().optional(),
  newGreenBeanProfile: z.any().optional(),
  roastInputWeight: z.number().optional(),
  roastTotalInputCost: z.number().optional(),
  packSizeGrams: z.number().optional(),
}).superRefine((data, ctx) => {
  if (data.itemType === 'green_bean') {
    if (data.reorderPoint === undefined || data.reorderPoint <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Reorder Point is required for Green Bean",
        path: ["reorderPoint"],
      });
    }

    const species = (data.green_bean_profile?.species ?? '').trim();
    if (!species) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Species is required",
        path: ["green_bean_profile", "species"],
      });
    }

    const processType = (data.green_bean_profile?.process_type ?? '').trim();
    if (!processType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Process Type is required",
        path: ["green_bean_profile", "process_type"],
      });
    }
  }
});

export type InventoryItemFormData = z.infer<typeof inventoryItemSchema> & {
  green_bean_profile?: CreateGreenBeanProfileDTO;
  newGreenBeanProfile?: CreateGreenBeanProfileDTO;
};

export interface InventoryItem extends InventoryItemFormData {
  id: string;
  stock: number;
  status: 'active' | 'inactive';
  expiryDate?: string;
  estimatedSellingPrice?: number;
  packSizeGrams?: number;
  greenBeanProfiles?: GreenBeanProfile[];
  roastProfiles?: RoastProfile[];
  cuppingProfiles?: CuppingProfile[];
  averagePrice?: number;
  ingredients?: InventoryItem[];
  // True for the tagged onboarding-sample items. Used to restrict item pickers to
  // sample-only data while the guided tour is in progress.
  isOnboardingSample?: boolean;
}

export interface InventoryLot {
  id: string;
  inventoryItemId: string;
  lotNumber: string;
  initialQuantity: number;
  remainingQuantity: number;
  expiryDate?: string;
  receivedDate: string;
  supplierId?: string;
  supplierName?: string;
  supplierEmail?: string;
  supplierPhone?: string;
  sourceType: string;
  sourceId?: string;
  sourceRef?: string;
  itemName?: string;
  itemCode?: string;
  unitPrice?: number; // Added per PRD
  isSample?: boolean;
  sampleId?: string;
  photoName?: string;
  photoUrl?: string;
  photoUploadedAt?: string;
  processType?: string | null;
}

export interface InventorySample {
  id: string;
  inventoryItemId: string;
  lotId?: string;
  lotNumber?: string;
  sampleNumber: string;
  date: string;
  notes?: string;
  createdAt: string;
  greenBeanProfileId?: string;
  roastProfileId?: string;
  cuppingProfileId?: string;
  qaPassed?: boolean | null;
}

export interface RoastedBulkGroupItem {
  workOrderId: string;
  workOrderNumber: string;
  inputWeight: number;
  outputWeight: number;
  shrinkageKg: number;
  shrinkagePercent: number | null;
  remainingQuantity: number;
  expiryDate?: string;
  status: string;
  inventoryItemId: string;
  lotId: string;
  unitPrice?: number;
  // unitPrice is the lot's TOTAL COGS; unitCostPerKg is that divided by the
  // lot's ORIGINAL quantity. Anything costing a partial draw uses the latter.
  unitCostPerKg?: number;
  // stock_levels.created_at — the order the server draws FIFO in. This list is
  // ordered by work order number, so sort on this when previewing a draw.
  stockCreatedAt?: string;
}

export interface RoastedBulkGroup {
  itemId: string;
  itemCode: string;
  itemName: string;
  roastType: string;
  totalStock: number;
  averagePrice: number;
  items?: RoastedBulkGroupItem[];
}

export type RoastedBulkAdjustmentReason = 'waste' | 'sample' | 'internal' | 'correction';

export interface RoastedBulkAdjustmentAllocation {
  inventoryItemId: string;
  workOrderNumber: string;
  quantityKg: number;
  unitCost: number;
  cost: number;
}

export interface RoastedBulkGroupAdjustmentResult {
  adjustmentId: string;
  totalQuantityKg: number;
  totalCost: number;
  allocations: RoastedBulkAdjustmentAllocation[];
}

export interface StockMovement {
  id: string;
  itemId: string;
  warehouseId: string;
  movementType: 'receipt' | 'issue' | 'transfer' | 'adjustment' | 'production';
  quantity: number;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  createdAt: string;
  warehouseName?: string;
  userName?: string;
  unitCost?: number;
  lotId?: string;
  lotNumber?: string;
}
